import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = "superadmin" | "admin" | "bendahara" | "guru";

// Module-level cache so navigating between routes never flashes a loading state.
let cachedRole: AppRole | null | undefined = undefined;
let cachedRoleUserId: string | null = null;
const listeners = new Set<(r: AppRole | null) => void>();
const STORAGE_PREFIX = "almunajah-role-cache:";

function readStoredRole(userId: string | null): AppRole | null | undefined {
  if (!userId || typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${userId}`);
    if (raw === null) return undefined;
    return raw === "" ? null : (raw as AppRole);
  } catch {
    return undefined;
  }
}

function writeStoredRole(userId: string | null, role: AppRole | null) {
  if (!userId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${userId}`, role ?? "");
  } catch { /* ignore */ }
}

function setCachedRole(r: AppRole | null) {
  cachedRole = r;
  writeStoredRole(cachedRoleUserId, r);
  listeners.forEach((fn) => fn(r));
}

export function primeUserRoleCache(userId: string | null | undefined) {
  const nextUserId = userId ?? null;
  if (nextUserId === cachedRoleUserId && cachedRole !== undefined) return;
  cachedRoleUserId = nextUserId;
  cachedRole = readStoredRole(nextUserId);
  if (cachedRole !== undefined) listeners.forEach((fn) => fn(cachedRole ?? null));
}

export function resetUserRoleCache() {
  cachedRoleUserId = null;
  cachedRole = undefined;
  listeners.forEach((fn) => fn(null));
}

export function useUserRole() {
  const [role, setRole] = useState<AppRole | null>(cachedRole ?? null);
  const [loading, setLoading] = useState(cachedRole === undefined);

  useEffect(() => {
    const listener = (r: AppRole | null) => setRole(r);
    listeners.add(listener);

    let mounted = true;
    const loadRole = async (silent = false) => {
      if (!silent) setLoading(true);
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) {
        cachedRoleUserId = null;
        if (mounted) { setCachedRole(null); setLoading(false); }
        return;
      }
      cachedRoleUserId = u.user.id;
      const stored = readStoredRole(u.user.id);
      if (stored !== undefined && cachedRole === undefined && mounted) {
        cachedRole = stored;
        setRole(stored);
        setLoading(false);
      }
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", u.user.id)
        .order("role", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (!mounted) return;
      setCachedRole((data?.role as AppRole) ?? null);
      setLoading(false);
    };

    if (cachedRole !== undefined) {
      setLoading(false);
      void loadRole(true);
    } else {
      void loadRole();
    }

    let lastUid: string | null = null;
    void supabase.auth.getUser().then(({ data }) => { lastUid = data.user?.id ?? null; });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        lastUid = null;
        cachedRoleUserId = null;
        setCachedRole(null);
        setLoading(false);
        return;
      }
      if (event === "SIGNED_IN" || event === "USER_UPDATED") {
        const uid = session?.user?.id ?? null;
        if (!uid) return;
        // User sama — role tidak berubah, jangan reload (biar tidak flash).
        if (uid === lastUid && cachedRole !== undefined) return;
        lastUid = uid;
        primeUserRoleCache(uid);
        void loadRole(cachedRole !== undefined);
      }
    });

    return () => {
      mounted = false;
      listeners.delete(listener);
      sub.subscription.unsubscribe();
    };
  }, []);

  const isAdmin = role === "admin" || role === "superadmin";
  return { role, loading, isAdmin };
}


/** Routes each role is allowed to access. */
export const ROLE_ROUTES: Record<AppRole, string[]> = {
  superadmin: ["*"],
  admin: ["*"],
  bendahara: [
    "/dashboard",
    "/students",
    "/gallery",
    "/payment-types",
    "/student-bills",
    "/payments",
    "/payment-settings",
    "/staff-announcements",
    "/chat",
  ],
  guru: ["/dashboard", "/attendance", "/gallery", "/schedules", "/staff-announcements", "/chat"],
};

export function canAccess(role: AppRole | null, path: string): boolean {
  if (!role) return false;
  // Dashboard & Profil selalu bisa diakses oleh semua role.
  if (path === "/dashboard" || path.startsWith("/dashboard/")) return true;
  if (path === "/profile" || path.startsWith("/profile/")) return true;
  const allowed = ROLE_ROUTES[role];
  if (!allowed) return false;
  if (allowed.includes("*")) return true;
  return allowed.some((p) => path === p || path.startsWith(p + "/"));
}
